import React from 'react';
import { useQuery } from '@apollo/client';
import { Card, Row, Col, Typography, Spin, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { GET_INSURANCE_PAYORS } from '../graphql/queries';
import { useTranslation } from '../hooks/useTranslation';
import { InsuranceOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const Insurance: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(GET_INSURANCE_PAYORS);

  const payors = data?.insurancePayors || [];

  const handlePayorClick = (payor: string) => {
    navigate(`/insurance/${payor}`);
  };

  const formatPayorName = (payor: string) => {
    // Convert 'townhealth' to 'Town Health'
    return payor
      .split('')
      .map((char, index) => {
        if (index === 0) return char.toUpperCase();
        if (char === char.toUpperCase() || payor[index - 1] === ' ') {
          return char;
        }
        return char;
      })
      .join('')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (str) => str.toUpperCase());
  };

  const getPayorLogo = (payor: string) => {
    if (payor === 'townhealth' || payor === 'TOWNHEALTH') {
      return '/images/Townhealth_Logo.png';
    }
    return null;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message={t('common.error')}
        description={error.message}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      <Title level={2} style={{ marginBottom: 24 }}>
        {t('navigation.insurance') || 'Insurance Payors'}
      </Title>

      {payors.length === 0 ? (
        <Card>
          <Text type="secondary">No insurance payors found.</Text>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {payors.map((payor: string) => {
            const logoPath = getPayorLogo(payor);
            return (
              <Col xs={24} sm={12} md={8} lg={6} key={payor}>
                <Card
                  hoverable
                  style={{
                    height: '100%',
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                  onClick={() => handlePayorClick(payor)}
                >
                  {logoPath ? (
                    <img
                      src={logoPath}
                      alt={`${formatPayorName(payor)} logo`}
                      style={{
                        maxWidth: '200px',
                        maxHeight: '120px',
                        marginBottom: 16,
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <InsuranceOutlined
                      style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }}
                    />
                  )}
                  <Title level={4} style={{ margin: 0 }}>
                    {formatPayorName(payor)}
                  </Title>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

export default Insurance;

